import type { CombinedError } from '@urql/core';

import type { ConfirmAdminMfaEnrolmentInput, VerifyAdminMfaInput } from '~/graphql/generated/graphql';

import { ADMIN_MFA_OPERATION_CONTEXT } from '~/constants/auth';
import {
  beginAdminMfaEnrolmentMutation,
  confirmAdminMfaEnrolmentMutation,
  verifyAdminMfaMutation,
} from '~/graphql/documents/core';
import { LoginFlowType } from '~/graphql/generated/graphql';
import { findGraphqlErrorWithCode, getFirstErrorMessageFromCombinedErrors } from '~/utils/graphql';

/**
 * What a second-factor step does next. `expired` means the pending sign-in lapsed (or the session ended),
 * so the only way on is to start the login again; `invalidCode` is a form error the step stays on;
 * `enrolmentRequired` and `alreadyEnrolled` mean the enrolment changed under the step, which moves to the
 * other one.
 */
export type AdminMfaOutcome
  = | { kind: 'verified' }
    | { kind: 'enrolled'; recoveryCodes: string[] }
    | { kind: 'enrolmentStarted'; enrolment: { secret: string; qrCodeSvg: string } }
    | { kind: 'invalidCode'; message: string }
    | { kind: 'expired' }
    | { kind: 'alreadyEnrolled' }
    | { kind: 'enrolmentRequired' }
    | { kind: 'failed' };

type Translate = (key: string, named?: Record<string, unknown>) => string;

const refusalOutcome = (t: Translate, error: CombinedError): Extract<AdminMfaOutcome, { kind: 'expired' | 'invalidCode' }> => {
  if (findGraphqlErrorWithCode(error, 'UNAUTHENTICATED')) {
    return { kind: 'expired' };
  }

  const rateLimitError = findGraphqlErrorWithCode(error, 'RATE_LIMIT_EXCEEDED');
  if (rateLimitError) {
    const retryAfter = Number(rateLimitError.extensions.retryAfter);
    return {
      kind: 'invalidCode',
      message: Number.isFinite(retryAfter) && retryAfter > 0
        ? t('general.auth.mfa.rateLimited', { count: retryAfter })
        : t('general.auth.mfa.rateLimitedShortly'),
    };
  }

  return { kind: 'invalidCode', message: getFirstErrorMessageFromCombinedErrors(t, error) };
};

/** The admin second-factor mutations the login flow's enrolment and challenge steps run. */
export const useAdminMfa = () => {
  const client = useUrqlClient();
  const { t } = useI18n();

  const invalidCode = (): AdminMfaOutcome => ({ kind: 'invalidCode', message: t('general.auth.mfa.invalidCode') });

  const beginEnrolment = async (): Promise<AdminMfaOutcome> => {
    const res = await client.mutation(beginAdminMfaEnrolmentMutation, {}, ADMIN_MFA_OPERATION_CONTEXT);

    if (res.error) {
      if (findGraphqlErrorWithCode(res.error, 'UNAUTHENTICATED')) {
        return { kind: 'expired' };
      }

      return findGraphqlErrorWithCode(res.error, 'ADMIN_MFA_ALREADY_ENROLLED') ? { kind: 'alreadyEnrolled' } : { kind: 'failed' };
    }

    const enrolment = res.data?.core.auth.beginAdminMfaEnrolment;

    return enrolment ? { kind: 'enrolmentStarted', enrolment } : { kind: 'failed' };
  };

  const confirmEnrolment = async (input: ConfirmAdminMfaEnrolmentInput): Promise<AdminMfaOutcome> => {
    const res = await client.mutation(confirmAdminMfaEnrolmentMutation, { input }, ADMIN_MFA_OPERATION_CONTEXT);

    if (res.error) {
      return findGraphqlErrorWithCode(res.error, 'ADMIN_MFA_ALREADY_ENROLLED') ? { kind: 'alreadyEnrolled' } : refusalOutcome(t, res.error);
    }

    const payload = res.data?.core.auth.confirmAdminMfaEnrolment;

    return payload?.flow === LoginFlowType.ExistingRole && payload.recoveryCodes
      ? { kind: 'enrolled', recoveryCodes: payload.recoveryCodes }
      : invalidCode();
  };

  const verify = async (input: VerifyAdminMfaInput): Promise<AdminMfaOutcome> => {
    const res = await client.mutation(verifyAdminMfaMutation, { input }, ADMIN_MFA_OPERATION_CONTEXT);

    if (res.error) {
      return refusalOutcome(t, res.error);
    }

    switch (res.data?.core.auth.verifyAdminMfa.flow) {
      case LoginFlowType.ExistingRole:
        return { kind: 'verified' };
      case LoginFlowType.MfaEnrolmentRequired:
        return { kind: 'enrolmentRequired' };
      default:
        return invalidCode();
    }
  };

  return { beginEnrolment, confirmEnrolment, verify };
};
